// Shared form components for consistency across the app
// Using Catalyst components and consistent styling from vessel setup

import React from 'react';
import { Checkbox as CatalystCheckbox, CheckboxField, Switch } from '../../catalyst/checkbox';

// Standard input styling from vessel setup
export const inputClassName = "block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm";

// Standard select styling from vessel setup
export const selectClassName = "block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm";

// Standard textarea styling (matching input pattern)
export const textareaClassName = "block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm resize-y";

// Standard label styling
export const labelClassName = "block text-sm font-medium text-gray-900";

// Required field indicator
export const RequiredIndicator = () => <span className="text-red-500">*</span>;

// Form input wrapper
export const FormField = ({ label, required, error, children, className = "" }) => (
  <div className={className}>
    {label && (
      <label className={labelClassName}>
        {label} {required && <RequiredIndicator />}
      </label>
    )}
    <div className="mt-2">
      {children}
    </div>
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

// Styled input component
export const Input = React.forwardRef(({ error, ...props }, ref) => (
  <input
    ref={ref}
    className={`${inputClassName} ${error ? 'outline-red-500 focus:outline-red-600' : ''}`}
    {...props}
  />
));

// Styled select component
export const Select = React.forwardRef(({ error, children, ...props }, ref) => (
  <select
    ref={ref}
    className={`${selectClassName} ${error ? 'outline-red-500 focus:outline-red-600' : ''}`}
    {...props}
  >
    {children}
  </select>
));

// Styled textarea component
export const Textarea = React.forwardRef(({ error, ...props }, ref) => (
  <textarea
    ref={ref}
    className={`${textareaClassName} ${error ? 'outline-red-500 focus:outline-red-600' : ''}`}
    {...props}
  />
));

// Checkbox using Catalyst component
export const Checkbox = ({ label, ...props }) => (
  <CheckboxField>
    <CatalystCheckbox {...props} />
    {label && <span className="text-sm text-gray-900">{label}</span>}
  </CheckboxField>
);

// Toggle switch using Catalyst Switch component
export const ToggleSwitch = ({ label, description, checked, onChange, ...props }) => (
  <div className="flex items-center justify-between">
    <div className="flex-grow">
      {label && <span className="text-sm font-medium text-gray-900">{label}</span>}
      {description && <p className="text-sm text-gray-500">{description}</p>}
    </div>
    <Switch
      checked={checked}
      onChange={onChange}
      {...props}
    />
  </div>
);

// Form section wrapper
export const FormSection = ({ title, description, children }) => (
  <div className="space-y-6">
    {(title || description) && (
      <div>
        {title && <h3 className="text-base font-semibold text-gray-900">{title}</h3>}
        {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
      </div>
    )}
    {children}
  </div>
);

// Card wrapper for form sections
export const FormCard = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6 ${className}`}>
    {children}
  </div>
);

// Button styles matching vessel setup
export const Button = ({ variant = 'primary', size = 'md', className = "", children, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-md transition-colors";
  
  const variants = {
    primary: "bg-cyan-600 text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500",
    secondary: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Grid layouts for forms
export const FormGrid = ({ columns = 2, children, className = "" }) => {
  const gridClasses = {
    1: "grid grid-cols-1 gap-6",
    2: "grid grid-cols-1 gap-6 sm:grid-cols-2",
    3: "grid grid-cols-1 gap-6 sm:grid-cols-3",
    4: "grid grid-cols-1 gap-6 sm:grid-cols-4",
    6: "grid grid-cols-1 gap-6 sm:grid-cols-6"
  };
  
  return (
    <div className={`${gridClasses[columns] || gridClasses[2]} ${className}`}>
      {children}
    </div>
  );
};